import type { ApolloClient } from "@apollo/client";
import { RECEIPT_TRACK_PHONES } from "../../../queries/receipt_track";

// Hasura caps how large an _in array can comfortably be, and reports can load
// tens of thousands of rows, so we page the reference_id lookups.
const CHUNK_SIZE = 1000;

const isUsablePhone = (phone?: string | null): boolean =>
  !!phone && phone.trim() !== "" && phone.trim().toUpperCase() !== "N/A";

/**
 * Looks up receipt_track phone numbers for a set of order reference ids and
 * returns a Map of reference_id -> phone_number. Only usable phone numbers are
 * included (non-empty, not "N/A"), and the most recent receipt_track row wins
 * (the query is ordered by created_at desc). Used to backfill the Phone Number
 * column on the transactions report and order search.
 */
export const fetchReceiptPhonesByReference = async (
  client: ApolloClient<object>,
  referenceIds: Array<string | number | null | undefined>,
  organizationId?: number | null
): Promise<Map<string, string>> => {
  const map = new Map<string, string>();

  const ids = Array.from(
    new Set(
      referenceIds
        .filter(
          (id): id is string | number =>
            id !== null && id !== undefined && id !== ""
        )
        .map((id) => String(id))
    )
  );
  if (ids.length === 0) return map;

  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    const chunk = ids.slice(i, i + CHUNK_SIZE);
    const where: any = { reference_id: { _in: chunk } };
    if (
      organizationId &&
      !Number.isNaN(organizationId) &&
      organizationId !== 0
    ) {
      where.organization_id = { _eq: organizationId };
    }

    const { data } = await client.query({
      query: RECEIPT_TRACK_PHONES,
      variables: { where },
      // no-cache: lookup rows are reduced to a Map and never read from the
      // Apollo cache; caching tens of thousands of them just leaks memory.
      fetchPolicy: "no-cache",
    });

    for (const row of data?.receipt_track ?? []) {
      const ref = row?.reference_id != null ? String(row.reference_id) : "";
      // Rows are ordered created_at desc, so the first usable phone we see for
      // a reference is the most recent one. Skip once we've recorded it.
      if (!ref || map.has(ref)) continue;
      if (isUsablePhone(row?.phone_number)) {
        map.set(ref, row.phone_number.trim());
      }
    }
  }

  return map;
};
